/**
 * Casino top ranking (providers / games) response parsers.
 *
 * The two top endpoints return the same shape — `data.rankings.<metric>[]`
 * — so they share a single normaliser. Casino metrics differ from sportsbook:
 * casino tracks `ggr` / `total_rounds` instead of `pnl` / `avg_bet_amount`.
 * Metric fields accept bare numbers (by-state / legacy) or the KPI-card
 * envelope `{ value, previous_value, percent_change }`.
 */

import {
  isRankingMetricField,
  parseRankingKpiMetric,
  type RankingKpiMetric,
} from "../../utils/kpiRankingMetric";

export type { RankingKpiMetric };

export type CasinoRankingRow = {
  name: string;
  /**
   * Two-letter abbreviation. Populated by the by-state endpoint (states key
   * the response by full name and the BE supplies the code); empty string
   * for provider / game endpoints where `abbr` is meaningless.
   */
  abbr: string;
  turnover: RankingKpiMetric;
  ggr: RankingKpiMetric;
  margin_pct: RankingKpiMetric;
  total_bets: RankingKpiMetric;
  total_rounds: RankingKpiMetric;
  total_users: RankingKpiMetric;
};

type RawRankingEntry = {
  name?: string;
  provider_name?: string;
  game_name?: string;
  state?: string;
  state_name?: string;
  abbr?: string;
  abbreviation?: string;
  state_abbr?: string;
  state_code?: string;
  turnover?: unknown;
  ggr?: unknown;
  margin_pct?: unknown;
  total_bets?: unknown;
  total_rounds?: unknown;
  total_users?: unknown;
};

const NESTED_ARRAY_KEYS = [
  "items",
  "rows",
  "providers",
  "top_providers",
  "games",
  "top_games",
] as const;

const RANKINGS_META_KEYS = new Set(["all", "meta", "filters", "summary"]);

/** Ranking groups exposed by the casino top endpoints / UI metric select. */
export const TOP_FIVE_METRIC_KEYS = [
  "turnover",
  "ggr",
  "margin_pct",
  "total_rounds",
  "total_users",
] as const;

export type CasinoTopFiveMetricKey = (typeof TOP_FIVE_METRIC_KEYS)[number];

export type CasinoTopFiveData = {
  byMetric: Partial<Record<CasinoTopFiveMetricKey, CasinoRankingRow[]>>;
};

const mapEntryToRow = (
  name: string,
  entry: RawRankingEntry,
): CasinoRankingRow => ({
  name:
    entry.name ??
    entry.state_name ??
    entry.state ??
    entry.provider_name ??
    entry.game_name ??
    name,
  abbr:
    entry.abbr ??
    entry.abbreviation ??
    entry.state_abbr ??
    entry.state_code ??
    "",
  turnover: parseRankingKpiMetric(entry.turnover),
  ggr: parseRankingKpiMetric(entry.ggr),
  margin_pct: parseRankingKpiMetric(entry.margin_pct),
  total_bets: parseRankingKpiMetric(entry.total_bets),
  total_rounds: parseRankingKpiMetric(entry.total_rounds),
  total_users: parseRankingKpiMetric(entry.total_users),
});

const isMetricEntry = (value: unknown): value is RawRankingEntry => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const entry = value as RawRankingEntry;
  return (
    isRankingMetricField(entry.turnover) ||
    isRankingMetricField(entry.ggr) ||
    isRankingMetricField(entry.margin_pct) ||
    isRankingMetricField(entry.total_bets) ||
    isRankingMetricField(entry.total_rounds) ||
    isRankingMetricField(entry.total_users)
  );
};

/**
 * v2 analytics endpoints can return either an array of rows or a keyed object
 * (`<provider/game name>` -> metrics). Coerce every supported shape into a
 * row array so the caller can map straight to chart datasets.
 */
export const normalizeCasinoRankingRows = (
  data: unknown,
): CasinoRankingRow[] => {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data.map((entry, index) => {
      if (isMetricEntry(entry)) {
        return mapEntryToRow(
          entry.name ??
            entry.provider_name ??
            entry.game_name ??
            `Row ${index + 1}`,
          entry,
        );
      }
      return mapEntryToRow(`Row ${index + 1}`, {});
    });
  }

  if (typeof data !== "object") return [];

  const record = data as Record<string, unknown>;
  for (const key of NESTED_ARRAY_KEYS) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return normalizeCasinoRankingRows(nested);
    }
  }

  return Object.entries(record)
    .filter(([, value]) => isMetricEntry(value))
    .map(([key, value]) => mapEntryToRow(key, value as RawRankingEntry));
};

const buildByMetricFromRows = (
  rows: CasinoRankingRow[],
): CasinoTopFiveData["byMetric"] => {
  if (rows.length === 0) return {};
  return TOP_FIVE_METRIC_KEYS.reduce<
    Partial<Record<CasinoTopFiveMetricKey, CasinoRankingRow[]>>
  >((acc, key) => {
    acc[key] = [...rows].sort((a, b) => b[key].value - a[key].value);
    return acc;
  }, {});
};

/**
 * Maps the top-providers / top-games envelope (`data.rankings.<metric>[]`)
 * into per-metric row lists. Falls back to building a uniform `byMetric`
 * map from a flat row array if the backend returns that simpler shape.
 */
export const parseCasinoTopFiveResponse = (
  body: unknown,
): CasinoTopFiveData => {
  const envelope = body as { data?: unknown } | null | undefined;
  const payload = envelope?.data ?? body;

  if (!payload || typeof payload !== "object") {
    return { byMetric: {} };
  }

  const record = payload as Record<string, unknown>;
  const rankings = record.rankings;

  if (rankings && typeof rankings === "object" && !Array.isArray(rankings)) {
    const byMetric: CasinoTopFiveData["byMetric"] = {};

    for (const [key, value] of Object.entries(
      rankings as Record<string, unknown>,
    )) {
      if (RANKINGS_META_KEYS.has(key) || !Array.isArray(value)) continue;
      byMetric[key as CasinoTopFiveMetricKey] =
        normalizeCasinoRankingRows(value);
    }

    return { byMetric };
  }

  return {
    byMetric: buildByMetricFromRows(normalizeCasinoRankingRows(payload)),
  };
};

/** Map a normalised row's value for a given metric (used by chart datasets). */
export const getMetricValue = (
  row: CasinoRankingRow,
  metric: CasinoTopFiveMetricKey,
): number => row[metric]?.value ?? 0;

export const getMetricPercentChange = (
  row: CasinoRankingRow,
  metric: CasinoTopFiveMetricKey,
): number | null => row[metric]?.percent_change ?? null;
